import IconTextUnderline from "@shared/components/icons/TextUnderline"
import { OPERATION_CONTRACTS } from "@shared/unified-tiptap/analytics/operations"
import { TEXT_COLORS } from "@shared/unified-tiptap/extensions/color/color-extension"
import { modKeyText } from "@shared/unified-tiptap/util/is-apple"
import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import type { CSSProperties } from "react"
import { CLEAR_SWATCH, pickerValue } from "./color-swatch-values"
import { ColorSplitControl, type SwatchItem } from "./ColorSplitControl"

const UNDERLINE_COLOR_OPTIONS = [{ name: "Default", color: null }, ...TEXT_COLORS]

function setUnderline(editor: Editor, args: { color: string | null; clear: boolean }): void {
  const labeled = editor
    .chain()
    .label(OPERATION_CONTRACTS.OP_SET_UNDERLINE_COLOR, "floating-toolbar", args)
  if (args.clear) labeled.unsetMark("underline").run()
  else labeled.setMark("underline", { color: args.color }).run()
}

export function UnderlinePopover({ editor }: { editor: Editor }) {
  /**
   * The active underline color (its `text-decoration-color`):
   * - `null` if underlined in the default color,
   * - `undefined` if not underlined, or no single color is universally active.
   *
   * Unlike text/highlight, `null` here means ACTIVE (a default-color underline), not
   * unset — the underline mark exists with no explicit color. That difference lives
   * only in this reader and in the items below; the shared control sees a color.
   */
  const activeUnderlineColor = useEditorState({
    editor,
    selector: (snapshot) => {
      if (!snapshot.editor.isActive("underline")) return undefined
      const color = (snapshot.editor.getAttributes("underline")?.color ?? null) as string | null
      return snapshot.editor.isActive("underline", { color }) ? color : undefined
    },
  })

  const isActive = activeUnderlineColor !== undefined
  const canToggle = useEditorState({
    editor,
    selector: (snapshot) => snapshot.editor.can().toggleMark("underline"),
  })

  const items: SwatchItem[] = UNDERLINE_COLOR_OPTIONS.map((entry) => ({
    color: entry.color ?? CLEAR_SWATCH,
    ariaLabel: `${entry.name} underline`,
    glyph: (
      // Keep the "U" neutral; color only the underline bar.
      <span
        className="[&_svg]:size-6!"
        style={{ "--underline-bar-color": entry.color ?? undefined } as CSSProperties}
      >
        <IconTextUnderline />
      </span>
    ),
    onSelect: () => setUnderline(editor, { color: entry.color, clear: false }),
  }))

  return (
    <ColorSplitControl
      isDisabled={!canToggle}
      isActive={isActive}
      activeAccent
      applyLabel="Underline"
      applyTooltip={
        <>
          Underline
          <span className="ml-2 text-gray-400">{modKeyText + "+U"}</span>
        </>
      }
      // The main button toggles underline in the default color; the picker sets a color.
      onApply={() => setUnderline(editor, { color: null, clear: isActive })}
      pickerLabel="Underline color"
      icon={
        <IconTextUnderline
          style={{ "--underline-bar-color": activeUnderlineColor ?? undefined } as CSSProperties}
        />
      }
      items={items}
      selectedColor={pickerValue(activeUnderlineColor)}
    />
  )
}
